import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Send } from 'lucide-react';
import { Badge, Button, Dialog, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { useCopyToClipboard } from '../../../shared/clipboard/useCopyToClipboard.js';
import { t } from '../../../shared/i18n/index.js';
import { staffApi, type PasswordResetDto, type StaffDto } from '../api/staff.api.js';

/**
 * PAROL TIKLASH oynasi.
 *
 * Ikki bosqichli va bu ataylab: tiklash — ORQAGA QAYTMAYDIGAN amal. Xodimning
 * eski paroli o'chadi va uning barcha ochiq seanslari yopiladi, ya'ni u
 * o'sha zahoti tizimdan chiqib qoladi. Bir bosishda bajarilsa, ro'yxatdagi
 * qo'shni qatorga tasodifan bosish shu oqibatni tug'dirardi.
 *
 * Yangi parol HAR DOIM shu ekranda bir marta ko'rsatiladi: administrator uni
 * xodimga o'zi beradi, xodim esa o'sha parol bilan kirib, birinchi ekranda
 * o'zinikini qo'yadi. Telegramga yuborilgan bo'lsa, bu qo'shimcha qator
 * bo'lib aytiladi — ilgari esa parol faqat botga ketib, ekranda umuman
 * ko'rinmasdi va bot yo'q joyda xodim tizimdan chiqib qolardi.
 */

export interface PasswordResetDialogProps {
  staff: StaffDto | null;
  onClose: () => void;
}

export const PasswordResetDialog = ({ staff, onClose }: PasswordResetDialogProps) => {
  const [result, setResult] = useState<PasswordResetDto | null>(null);
  const [copied, setCopied] = useState(false);
  const { copy: copyText } = useCopyToClipboard();

  const reset = useMutation({
    mutationFn: (userId: string) => staffApi.resetPassword(userId),
    onSuccess: setResult,
  });

  // Har ochilishda toza boshlanadi — oldingi xodimning paroli qolib ketmasin.
  useEffect(() => {
    if (staff === null) return;
    setResult(null);
    setCopied(false);
    reset.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `reset` har chizishda yangi
  }, [staff]);

  const error =
    reset.error === null || reset.error === undefined
      ? null
      : reset.error instanceof ApiClientError
        ? reset.error.message
        : t('staff.resetFailed');

  /* Belgi ATAYLAB turib qoladi (oyna yopilguncha): parolni nusxalagan
     administrator uni yetkazgunicha "nusxalandimi?" degan savolga javob
     ekranda turishi kerak. Shuning uchun hook ning o'z `copied` i emas,
     shu yerdagi holat ishlatiladi — undan faqat nusxalash olinadi. */
  const copy = (value: string): void => {
    void copyText(value).then((ok) => {
      if (ok) setCopied(true);
    });
  };

  return (
    <Dialog
      open={staff !== null}
      onClose={onClose}
      title={t('staff.resetTitle')}
      footer={
        result === null ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('staff.resetCancel')}
            </Button>
            <Button
              variant="danger"
              loading={reset.isPending}
              onClick={() => staff !== null && reset.mutate(staff.id)}
            >
              {t('staff.resetConfirm')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{t('staff.resetDone')}</Button>
        )
      }
    >
      {staff === null ? null : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-text font-medium">{staff.fullName}</span>
            <span className="tabular text-text-muted text-sm">{staff.username}</span>
          </div>

          {result === null ? (
            <>
              {/* Oqibatlari RO'YXAT bo'lib turadi: "ishonchingiz komilmi?" degan
                  savol odamga nima bo'lishini aytmaydi. */}
              <ul className="text-text-muted flex flex-col gap-1.5 text-sm">
                {['session', 'change', 'delivery'].map((key) => (
                  <li key={key} className="flex items-start gap-2">
                    <span aria-hidden className="text-text-faint mt-1.5 size-1 shrink-0 rounded-full bg-current" />
                    <span className="text-pretty">
                      {t(key === 'delivery' ? 'staff.resetWillScreen' : `staff.resetWill.${key}`)}
                    </span>
                  </li>
                ))}
              </ul>

              {error === null ? null : (
                <p
                  role="alert"
                  className="border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
                >
                  {error}
                </p>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="border-warn/30 bg-warn-soft text-warn flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm">
                <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
                <span className="text-pretty">{t('staff.resetOnScreen')}</span>
              </p>

              <div
                className={cn(
                  'border-border bg-surface-2 flex items-center justify-between gap-3',
                  'rounded-[var(--radius-sm)] border px-3 py-2.5',
                )}
              >
                <code className="tabular text-text text-base font-semibold tracking-wide">
                  {result.password}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copy(result.password)}
                >
                  {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                  {copied ? t('staff.resetCopied') : t('staff.resetCopy')}
                </Button>
              </div>

              <Badge tone="warn">{t('staff.resetOnce')}</Badge>

              {/* Telegram — QO'SHIMCHA nusxa, yagona yo'l emas. */}
              {result.delivery === 'telegram' ? (
                <p className="text-text-muted flex items-start gap-2 text-sm">
                  <Send size={16} aria-hidden className="text-accent mt-0.5 shrink-0" />
                  <span className="text-pretty">{t('staff.resetSentTelegram')}</span>
                </p>
              ) : null}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
